import { isEmbeddedMode } from "@/utils/embedded-mode";

/**
 * Messages the embedded page posts to the mobile app through the
 * react-native-webview bridge, as `JSON.stringify({ type, ...payload })`.
 *
 * `HOST_PAGE_READY_MESSAGE` is posted once per page load, after the first
 * screen the user can see has been painted (a settings page or the "session
 * ended" screen), so the app can take down its own loader.
 */
export const HOST_PAGE_READY_MESSAGE = "mela:page-ready";

export interface MobileHostBridge {
  postMessage(message: string): void;
}

export const getMobileHostBridge = (): MobileHostBridge | null => {
  if (!isEmbeddedMode()) return null;

  const { ReactNativeWebView } = window as Window & {
    ReactNativeWebView?: Partial<MobileHostBridge>;
  };
  return typeof ReactNativeWebView?.postMessage === "function"
    ? (ReactNativeWebView as MobileHostBridge)
    : null;
};

/** Returns `false` when there is no app to tell or the post failed. */
export const postToMobileHost = (
  type: string,
  payload: Record<string, unknown> = {},
): boolean => {
  const bridge = getMobileHostBridge();
  if (!bridge) return false;

  try {
    bridge.postMessage(JSON.stringify({ type, ...payload }));
    return true;
  } catch {
    return false;
  }
};

let pageReadySent = false;

export const notifyMobileHostPageReady = () => {
  if (pageReadySent) return;
  pageReadySent = postToMobileHost(HOST_PAGE_READY_MESSAGE, {
    path: window.location.pathname,
  });
};

/** Test-only: lets each test start from a fresh page load. */
export const resetMobileHostPageReady = () => {
  pageReadySent = false;
};
